"use client";

import { useState } from "react";
import Defination from "@/types/Definition";
import { useRouter } from "next/navigation";
import { toast } from "react-toastify";
import {
  Button,
  ButtonGroup,
  ButtonToolbar,
  Card,
  Col,
  Form,
  OverlayTrigger,
  Row,
  Table,
  Tooltip,
} from "react-bootstrap";
import * as Icon from "react-bootstrap-icons";
import {
  createOrUpdateSuggestion,
  requestToSearchDefinitions,
} from "@/app/glossary/actions";
import APIResponse from "@/types/APIResponse";

export const AlphabetSearchButtons = ({ onSearch, search }: any) => {
  // Generate an array of alphabets from A to Z
  const alphabets = Array.from({ length: 26 }, (_, i) =>
    String.fromCharCode(i + 65)
  );

  const handleButtonClick = (
    letter: string,
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault(); // Prevent any default form submission or other behaviors
    const formData = new FormData();
    formData.append("starts_with", letter);
    onSearch(formData);
  };

  return (
    <div className="tw-flex flex-wrap tw-justify-between tw-items-center tw-mb-2">
      {alphabets.map((letter, index) => (
        <Button
          variant="light"
          key={index}
          type="button"
          active={search == letter}
          onClick={(e) => handleButtonClick(letter, e)}
        >
          {letter}
        </Button>
      ))}
    </div>
  );
};

export default function DefinitionList({ records, isVerified }: any) {
  const router = useRouter();

  const [editorId, setEditorId] = useState(-1);
  const [message, setMessage] = useState("");
  const [suggestion, setSuggestion] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [apiResponse, setApiResponse] = useState(records);
  const [displayCount, setDisplayCount] = useState(20); // Initialize to show 20 entries
  const [nextUrl, setNextUrl] = useState(records?.next);
  const [currentRecords, setCurrentRecords] = useState(
    records?.results?.slice(0, 20) || []
  );

  function handleEditorId(id: number) {
    setEditorId(editorId === id ? -1 : id);
    setSuggestion("");
  }

  const showMore = async () => {
    if (currentRecords.length < apiResponse.results.length) {
      // If there are more records locally that haven't been shown yet
      const nextRecordsCount = Math.min(
        apiResponse.results.length,
        displayCount + 5,
        apiResponse?.count | 0 // Ensure not to exceed the total count of records
      );
      setCurrentRecords(apiResponse.results.slice(0, nextRecordsCount));
      setDisplayCount(nextRecordsCount);
    } else if (nextUrl) {
      // If no more local records and there is a URL to fetch more
      try {
        const response = await fetch(nextUrl);
        const data = await response.json();
        const updatedResults = [...apiResponse.results, ...data.results];
        setApiResponse({
          ...data,
          results: updatedResults,
        });
        // Calculate the new display count without exceeding the total count
        const newDisplayCount = Math.min(
          displayCount + 5,
          apiResponse?.count | 0
        );
        setCurrentRecords(updatedResults.slice(0, newDisplayCount));
        setDisplayCount(newDisplayCount);
        setNextUrl(data.next);
      } catch (error) {
        console.error("Failed to load more records:", error);
        toast.error("Error loading more records");
      }
    }
  };

  const handleSubmit = async (event: any) => {
    event.preventDefault();
    setMessage("Processing...");

    try {
      const response = await createOrUpdateSuggestion(editorId, suggestion);
      if (response) {
        setMessage("");
        toast.success("Thank you for your suggestion");
        handleEditorId(-1);
        router.refresh();
      }
    } catch (error) {
      console.error(error);
      setMessage("Error: Failed to send your suggestion");
      toast.error("Error: Failed to send your suggestion");
    }
  };

  async function onSearch(formData: FormData) {
    const search: any = formData.get("starts_with")
      ? formData.get("starts_with")
      : "";
    setSearchTerm(search);
    try {
      const response: APIResponse<any> = await requestToSearchDefinitions(
        formData
      );
      setApiResponse(response);
      setCurrentRecords(response?.results?.slice(0, 20) || []);
      setDisplayCount(Math.min(response?.results?.length || 0, 20));
      setNextUrl(response?.next || "");
    } catch (error) {
      console.error("Failed to search", error);
      toast.error("Error: please try again later...");
    }
  }

  const renderTooltip = (props: any) => (
    // NOTE: adding fixed position to stop screen flashing on tooltip render: https://stackoverflow.com/questions/74726036/how-to-fix-bootstrap-react-tooltip-blinking-scrolled-in-body
    <Tooltip id="button-tooltip" style={{ position: "fixed" }} {...props}>
      {!isVerified ? "Register on AI2Peat to access this content." : "Have a suggestion for us?"}
    </Tooltip>
  );

  return (
    <Card>
      <Card.Header>
        <Form action={onSearch}>
          <Row className="tw-items-center">
            <Col sm={4} className="my-1">
              <Form.Label htmlFor="term" visuallyHidden>
                Term
              </Form.Label>
              <Form.Control placeholder="Search Term" id="term" name="term" />
            </Col>
            <Col sm={4} className="my-1">
              <Form.Select aria-label="Select a theme" id="theme" name="theme">
                <option value="">Select the theme</option>
                {[
                  "Air",
                  "Archaeology",
                  "Biodiversity",
                  "Climate",
                  "Energy",
                  "General",
                  "Horticulture",
                  "Land use",
                  "Management",
                  "Monitoring",
                  "Peatland / Habitat type",
                  "Pollution",
                  "Society and Culture",
                  "Soil",
                  "Technology",
                  "Water",
                  "Wetland",
                ].map((theme, index) => (
                  <option key={index} value={theme}>
                    {theme}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col
              xs="auto"
              className="tw-flex tw-items-center tw-justify-between tw-my-1"
            >
              <Button variant="outline-primary" type="submit">
                Search
              </Button>
              <p className="tw-ml-4">
                {apiResponse?.count | 0}{" "}
                {apiResponse?.count === 1 ? "term" : "terms"} listed
              </p>
            </Col>
          </Row>
        </Form>
      </Card.Header>
      <Card.Body className="tw-px-0">
        <AlphabetSearchButtons onSearch={onSearch} search={searchTerm} />
        <Table responsive striped>
          <thead>
            <tr className="font-weight-bold">
              <th scope="col" className="table-dark">
                Term
              </th>
              <th scope="col" className="table-dark">
                Theme
              </th>
              <th scope="col" className="table-dark">
                Description
              </th>
              <th scope="col" className="table-dark"></th>
            </tr>
          </thead>
          <tbody>
            {apiResponse?.count === 0 || !apiResponse?.results ? (
              <tr>
                <td colSpan={4}>No terms found</td>
              </tr>
            ) : null}
            {apiResponse?.results
              .slice(0, displayCount)
              .map((record: Defination, index: number) => (
                <tr key={index}>
                  <td>{record.term}</td>
                  <td>{record.themes}</td>
                  <td>
                    <p>{record.description}</p>
                    <div hidden={editorId != record.id} className="tw-mt-3">
                      <Card>
                        <Card.Header>
                          Suggestion for &quot;{record.term}&quot;
                        </Card.Header>
                        <Card.Body>
                          <Form onSubmit={handleSubmit}>
                            <Form.Group
                              as={Row}
                              className="tw-mb-3"
                              controlId="suggestion"
                            >
                              <Col sm="12">
                                <Form.Control
                                  as="textarea"
                                  rows={3}
                                  placeholder="Start typing here..."
                                  value={suggestion}
                                  onChange={(event) => {
                                    setSuggestion(event.target.value);
                                  }}
                                  required
                                />
                              </Col>
                            </Form.Group>
                            <div>
                              <Button
                                variant="primary"
                                type="submit"
                                className="tw-mb-3"
                              >
                                Submit
                              </Button>
                              <div>
                                <Form.Text
                                  id="message"
                                  className="cr-field-invalid"
                                >
                                  {message}
                                </Form.Text>
                              </div>
                            </div>
                          </Form>
                        </Card.Body>
                      </Card>
                    </div>
                  </td>
                  <td>
                    <ButtonToolbar>
                      <ButtonGroup>
                        <OverlayTrigger
                          placement="top"
                          delay={{ show: 250, hide: 400 }}
                          overlay={renderTooltip}
                        >
                          <div className="tw-mt-5">
                            <Button
                              variant="light"
                              onClick={() => handleEditorId(record.id)}
                              active={true}
                              disabled={!isVerified}
                            >
                              <Icon.ChatLeftText />
                            </Button>
                          </div>
                        </OverlayTrigger>
                      </ButtonGroup>
                    </ButtonToolbar>
                  </td>
                </tr>
              ))}
          </tbody>
        </Table>
      </Card.Body>
      <Card.Footer>
        <div className="tw-flex tw-justify-between tw-items-center tw-pt-3 tw-pb-3">
          <div style={{ flex: 1 }}></div> {/* Empty div for spacing */}
          {apiResponse?.count > displayCount && (
            <button
              onClick={showMore}
              className="btn btn-primary"
              style={{ flex: 1 }}
            >
              Show More
            </button>
          )}
          <div style={{ flex: 1, textAlign: "right" }}>
            {displayCount} / {apiResponse?.count || 0}
          </div>
        </div>
      </Card.Footer>
    </Card>
  );
}
